import Grid from "@components/Grid/Grid";
import { NextContentfulImage } from "@components/Image";
import Link from "@components/Link/Link";
import { TypeFooterFields } from "@customTypes/contentful";
import { COVEO_IGNORE_CLASS } from "@utilities/coveoConst";
import setGADataLayer, { dataLayerEventKey } from "@utilities/setGADataLayer";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import Cta from "../Cta/Cta";
import styles from "./C02_Footer.module.scss";

type Props = { displayFooter?: boolean } & TypeFooterFields;

export const Footer = ({ content }: { content: Props }) => {
  const {
    primaryLinksColumn1,
    primaryLinksColumn2,
    primaryLinksColumn3,
    secondaryLinks,
    ctaHeading,
    ctaText,
    ctaLink,
    socialHeading,
    socialLinks,
    copyrightText,
  } = content;

  const router = useRouter();
  const shouldMoveFocusAfterNavigation = useRef(false);

  const ctaId = `footer-sign-up-label`;

  let pageURL = router?.asPath;
  pageURL = pageURL === "/" ? pageURL.replace("/", "") : pageURL;

  const setGAClick = (name: string, destination: string) => {
    setGADataLayer({
      data: {
        name: name,
        referring_page:
          typeof window !== "undefined" ? window.location.href : "",
        destination_page: destination,
      },
      _clear: true,
      event: dataLayerEventKey.FOOTER,
    });
  };

  const focusSkipNav = () => {
    requestAnimationFrame(() => {
      const skipNav = document.getElementById("skipnav") as HTMLElement | null;

      if (!skipNav) return;

      skipNav.focus({ preventScroll: true });
      window.scrollTo({ top: 0, left: 0 });
    });
  };

  useEffect(() => {
    const handleRouteChangeComplete = (url: string) => {
      if (!shouldMoveFocusAfterNavigation.current) return;

      shouldMoveFocusAfterNavigation.current = false;

      if (url.includes("#")) return;

      focusSkipNav();
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router.events]);

  const NavLink = ({ link }) => {
    const href = link.fields ? link.fields.url : link.url;
    const text = link.fields ? link.fields.linkText : link.linkText;

    return (
      <li>
        <Link
          href={href}
          onClick={() => setGAClick(text, href)}
          onNavigate={() => {
            shouldMoveFocusAfterNavigation.current = true;
          }}
        >
          {text}
        </Link>
      </li>
    );
  };

  return (
    <footer className={clsx(styles["site-footer"], COVEO_IGNORE_CLASS)}>
      <div className="wrapper">
        {content?.displayFooter && (
          <div className={styles.top}>
            <Grid row>
              <Grid column sm={12} md={3}>
                <ul>
                  {primaryLinksColumn1?.map((link, i) => (
                    <NavLink link={link} key={`${i}${link.fields?.linkText}`} />
                  ))}
                </ul>
              </Grid>
              <Grid column sm={12} md={3}>
                <ul>
                  {primaryLinksColumn2?.map((link, i) => (
                    <NavLink link={link} key={`${i}${link.fields?.linkText}`} />
                  ))}
                </ul>
              </Grid>
              <Grid column sm={12} md={3}>
                <ul>
                  {primaryLinksColumn3?.map((link, i) => (
                    <NavLink link={link} key={`${i}${link.fields?.linkText}`} />
                  ))}
                </ul>
              </Grid>
              <Grid column sm={12} md={3}>
                <div className={styles.ctas}>
                  <p id={ctaId}>{ctaHeading}</p>
                  <Cta
                    type="secondary"
                    light={true}
                    url={pageURL?.split("?")[0].split("#")[0] + ctaLink}
                    linkText={ctaText}
                    preventDefault={false}
                    ariaDescribedBy={ctaId}
                    onClick={() =>
                      setGAClick(
                        ctaText,
                        pageURL?.split("?")[0].split("#")[0] + ctaLink
                      )
                    }
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        )}
        {content?.displayFooter && (
          <div className={styles.social}>
            <Grid row>
              <Grid column sm={12}>
                <p className={clsx(styles.large, "large")}>{socialHeading}</p>
              </Grid>
              <Grid column sm={12}>
                <ul>
                  {socialLinks.map(({ fields }, i) => {
                    return (
                      <li key={`${i}${fields.title}`}>
                        <a
                          href={fields.url}
                          aria-label={fields.title}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() =>
                            setGADataLayer({
                              data: {
                                platform: fields.title,
                              },
                              _clear: true,
                              event: dataLayerEventKey.SOCIAL_CLICKOUT,
                            })
                          }
                        >
                          <div
                            className={clsx(styles.socialIcon, "image-hover")}
                          >
                            <NextContentfulImage
                              src={fields.icon.fields.file.url}
                              alt={`${fields.title} icon`}
                              width={40}
                              height={40}
                              role="presentation"
                              aria-hidden="true"
                            />
                          </div>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </Grid>
            </Grid>
          </div>
        )}
        <div className={styles.bottom}>
          <Grid row>
            <Grid column md={9} sm={12}>
              <ul>
                {secondaryLinks.map((link, i) => (
                  <NavLink link={link} key={`${i}${link?.linkText}`} />
                ))}
              </ul>
            </Grid>
            <Grid column md={3} sm={12}>
              <div className={styles.copyright}>
                <p>{copyrightText}</p>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
